import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Avatar,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Group as GroupIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

// 颜色常量
const COLORS = ['#1976d2', '#4caf50', '#ff9800', '#9c27b0', '#e91e63'];

// 生成模拟数据
const generateBranchData = () => {
  return {
    id: 'downtown',
    name: 'Downtown Branch',
    location: 'Vancouver Downtown',
    metrics: {
      monthlyPolicies: 220,
      annualSubmit: 2640,
      monthlyPrem: 550000,
      totalBrokers: 60,
      avgQualityScore: 92,
      totalHighQuality: 180
    },
    brokers: Array(60).fill(null).map((_, index) => ({
      id: `broker-${index + 1}`,
      name: `Broker ${index + 1}`,
      avatar: `B${index + 1}`,
      monthlyPolicies: 15 + Math.floor(Math.random() * 10),
      autoSubmit: 8 + Math.floor(Math.random() * 5),
      homeSubmit: 4 + Math.floor(Math.random() * 3),
      commercialSubmit: 2 + Math.floor(Math.random() * 2),
      qualityScore: 85 + Math.floor(Math.random() * 10),
      monthlyPrem: (35000 + Math.floor(Math.random() * 15000)).toFixed(2)
    }))
  };
};

const ITEMS_PER_PAGE = 10;

const BranchPerformanceDashboard = () => {
  const [timeRange, setTimeRange] = useState('monthly');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const branchData = generateBranchData();

  // 过滤经纪人数据
  const filteredBrokers = useMemo(() => {
    return branchData.brokers.filter(broker =>
      broker.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [branchData.brokers, searchTerm]);

  // 分页处理
  const paginatedBrokers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredBrokers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredBrokers, currentPage]);

  // 生成图表数据
  const generatePolicyTypeData = () => {
    const totalAuto = branchData.brokers.reduce((sum, broker) => sum + broker.autoSubmit, 0);
    const totalHome = branchData.brokers.reduce((sum, broker) => sum + broker.homeSubmit, 0);
    const totalCommercial = branchData.brokers.reduce((sum, broker) => sum + broker.commercialSubmit, 0);

    return [
      { name: 'Auto', value: totalAuto },
      { name: 'Home', value: totalHome },
      { name: 'Commercial', value: totalCommercial }
    ];
  };

  const generateMonthlyTrendData = () => {
    return Array.from({ length: 12 }, (_, i) => ({
      month: new Date(2024, i).toLocaleString('en-US', { month: 'short' }),
      policies: Math.floor(180 + Math.random() * 40),
      premium: Math.floor(450000 + Math.random() * 100000)
    }));
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        {/* 标题和分支信息 */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ 
            color: '#1976d2', 
            mb: 1,
            fontWeight: 600 
          }}>
            {branchData.name} Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Location: {branchData.location}
          </Typography>
        </Box>

        {/* 控制面板 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="annual">Annual</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search brokers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>

        {/* 关键指标卡片 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {Object.entries(branchData.metrics).map(([key, value]) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={key}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </Typography>
                  <Typography variant="h6">
                    {key.includes('Prem') ? `$${value.toLocaleString()}` :
                     key.includes('Score') ? `${value}%` :
                     value.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 图表区域 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* 保单类型分布 */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Policy Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={generatePolicyTypeData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {generatePolicyTypeData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* 月度趋势 */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Monthly Trends
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={generateMonthlyTrendData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="policies" fill="#1976d2" name="Policies" />
                  <Bar yAxisId="right" dataKey="premium" fill="#4caf50" name="Premium" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* 经纪人表格 */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2 
          }}>
            <Typography variant="h6" sx={{ color: '#1976d2' }}>
              Broker Performance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Brokers: {filteredBrokers.length}
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Broker</TableCell>
                  <TableCell align="center">Monthly Policies</TableCell>
                  <TableCell align="center">Auto</TableCell>
                  <TableCell align="center">Home</TableCell>
                  <TableCell align="center">Commercial</TableCell>
                  <TableCell align="center">Premium</TableCell>
                  <TableCell align="center">Quality Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBrokers.map((broker) => (
                  <TableRow key={broker.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: '#e3f2fd', color: '#1976d2' }}>
                          {broker.avatar}
                        </Avatar>
                        <Typography variant="body2">{broker.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{broker.monthlyPolicies}</TableCell>
                    <TableCell align="center">{broker.autoSubmit}</TableCell>
                    <TableCell align="center">{broker.homeSubmit}</TableCell>
                    <TableCell align="center">{broker.commercialSubmit}</TableCell>
                    <TableCell align="center">${Number(broker.monthlyPrem).toLocaleString()}</TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{
                          color: broker.qualityScore >= 90 ? '#2e7d32' :
                                broker.qualityScore >= 80 ? '#ed6c02' : '#d32f2f'
                        }}
                      >
                        {broker.qualityScore}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              count={Math.ceil(filteredBrokers.length / ITEMS_PER_PAGE)}
              page={currentPage}
              onChange={(_, value) => setCurrentPage(value)}
              color="primary"
            />
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default BranchPerformanceDashboard; 